import type { KeyboardEvent, ReactNode } from 'react'
import { useState } from 'react'
import type { TrayCard } from '../_lib/auto-place'
import type { SourceOption } from '../_lib/source-option'
import type { CardDetailsSaveResult } from '../_state/contracts'
import { SourceGlyph } from './source-glyph'
import { SourceMark } from './source-mark'

/**
 * The source switcher on the card-details title row (LG-360 §7) — a coach's
 * way to change which source a card's problem is assigned from.
 *
 * Collapsed, it reads as `SourceIconStack` (`components/admin/shared/`)
 * already does elsewhere: one mark standing for the card's current source.
 * Clicking it spreads the other candidate sources out from behind it —
 * `-space-x-1` (overlapped) to `space-x-1`, with the revealed buttons going
 * `scale-95 opacity-0` to `scale-100 opacity-100` — rather than opening a
 * floating layer, since there is no animation library in this repo to lean
 * on and a popover would be a second dismissable layer nested inside the
 * dialog's own.
 *
 * Never offers the source the card already wears: `TrayCard.sourceId` is the
 * coach's explicit pick, and `null` means the card wears whichever option the
 * reader marked `owning`. Picking either back would be a no-op write that
 * reads as a broken option once it did nothing.
 *
 * Renders no button at all when there is only one candidate source — a
 * problem listed in exactly one place has no switch to make, and a button
 * with a single, already-current destination is a dead one.
 *
 * Escape collapses the switcher on its own — an ordinary bubble-phase
 * `onKeyDown`, nothing exotic. Stopping that Escape from *also* closing the
 * dialog this switcher sits inside is deliberately not this component's job:
 * it reports its `expanded` state up via `onExpandedChange`, and the caller
 * gates its own `DialogContent`'s `onEscapeKeyDown` on it (see
 * `card-details-modal.tsx`), the same one-line idiom
 * `profile-editor-dialog.tsx` already uses for its `saving` gate. That is
 * reachable and authoritative — it runs inside Radix's own capture-phase
 * Escape handling, so `preventDefault` there is checked before Radix ever
 * dismisses — where a listener owned by this component could only ever race
 * Radix's for the same keystroke, and would swallow Escape for *any* dialog
 * this switcher happened to be mounted inside, including one stacked above it.
 */
export function SourceSwitcher(props: {
  card: TrayCard
  /**
   * The wire shape, not the server DTO (LG-360 expert review, B7) — see
   * `SourceOption`'s doc in `_lib/source-option.ts` for why that distinction
   * matters even though neither type currently differs in practice.
   */
  options: readonly SourceOption[]
  onPick: (sourceId: string | null) => Promise<CardDetailsSaveResult>
  /** Told whenever `expanded` changes — see the module doc for why. */
  onExpandedChange?: (expanded: boolean) => void
}): ReactNode {
  const { card, options, onPick, onExpandedChange } = props

  // `card.sourceId === null` never matches a real option (every
  // `ProblemSourceOptionDto.sourceId` is a string), so it falls straight
  // through to the `owning` fallback below — which is exactly "the card
  // wears whichever option the reader marked `owning`" when there is no
  // explicit pick. No separate null-coalesce for that case is needed: this
  // one chain covers both "explicit pick" and "implicit owning" already.
  // `.at(0)`, not `[0]`: with noUncheckedIndexedAccess off, `[0]` would type
  // `current` as always-present and make the `current === undefined` guard
  // below look dead to the checker — while an empty `options` list makes it
  // load-bearing at runtime.
  const current =
    options.find((option) => option.sourceId === card.sourceId) ??
    options.find((option) => option.owning) ??
    options.at(0)

  const others = options.filter(
    (option) => option.sourceId !== current?.sourceId,
  )

  const [expanded, setExpandedState] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The one place `expanded` actually changes, so `onExpandedChange` can
  // never drift out of sync with what this component renders.
  const setExpanded = (next: boolean): void => {
    setExpandedState(next)
    onExpandedChange?.(next)
  }

  if (current === undefined) return null

  if (others.length === 0) {
    return (
      <span className="flex items-center gap-1 text-[9.5px] font-semibold text-(--cd-text-muted)">
        <SourceMark decorative icon={current.icon} />
        {current.displayName}
      </span>
    )
  }

  const handlePick = async (sourceId: string): Promise<void> => {
    if (saving) return
    setSaving(true)
    setError(null)
    const result = await onPick(sourceId)
    setSaving(false)
    // A failure stays expanded and reports next to the control — collapsing
    // here would hide the very buttons the coach would retry from, and the
    // `role="status"` line below is what a version that collapsed on error
    // would otherwise have nowhere left to show.
    if (result.error === null) setExpanded(false)
    else setError(result.error)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Escape' || !expanded) return
    setExpanded(false)
  }

  return (
    <div className="flex items-center gap-1.5" onKeyDown={handleKeyDown}>
      <div
        className={`flex items-center transition-all duration-150 ease-out motion-reduce:transition-none ${
          expanded ? 'space-x-1' : '-space-x-1'
        }`}
      >
        <button
          aria-expanded={expanded}
          aria-label={`Change the source, currently ${current.displayName}`}
          className="cursor-pointer rounded-full border-0 bg-transparent p-0"
          onClick={() => setExpanded(!expanded)}
          type="button"
        >
          <SourceGlyph displayName={current.displayName} icon={current.icon} />
        </button>
        {others.map((option) => (
          <button
            aria-label={`Assign from ${option.displayName}`}
            className={`cursor-pointer rounded-full border-0 bg-transparent p-0 transition-all duration-150 ease-out motion-reduce:transition-none ${
              expanded
                ? 'scale-100 opacity-100'
                : 'pointer-events-none scale-95 opacity-0'
            }`}
            // A real `disabled`, not `aria-disabled`: Radix focuses the
            // first tabbable element in the dialog on open, and filters on
            // the `disabled` property — a collapsed, invisible option left
            // tabbable would be a candidate for that opening focus.
            disabled={!expanded || saving}
            key={option.sourceId}
            onClick={() => void handlePick(option.sourceId)}
            type="button"
          >
            <SourceGlyph displayName={option.displayName} icon={option.icon} />
          </button>
        ))}
      </div>
      {/* Same `role="status"` idiom as `NoteEditor`/`CompletionControl`/
          `ProblemHistorySection`'s error lines: unconditionally present so
          the accessible name stays stable, empty when there is nothing to
          report. */}
      <span
        aria-live="polite"
        className="text-[10px] text-(--cd-red)"
        role="status"
      >
        {error ?? ''}
      </span>
    </div>
  )
}
